/**
 * AI Panel Assistant Service
 *
 * Provides the knowledge base and question-answering logic for the floating
 * AI assistant (playwinadmin only, and only after the profile-picture unlock).
 *
 * ============================================================================
 * HOW TO ADD ANSWERS
 * ============================================================================
 * Add entries to KNOWLEDGE_BASE below. Each entry has:
 *   - keywords: array of trigger words/phrases (any language). Matching is
 *     case-insensitive and diacritic-insensitive. Include both English and
 *     Arabic keywords so the question is found in either language.
 *   - answer: { en: '...', ar: '...' } — the assistant replies in the
 *     language the question was asked in (falls back to English).
 * ============================================================================
 */

// Username allowed to see/use the assistant
export const AI_ASSISTANT_USERS = ['playwinadmin'];

export const isAIAssistantUser = (username) =>
  AI_ASSISTANT_USERS.includes((username || '').toLowerCase());

/** Profile-picture presses required before the assistant button appears. */
export const AI_ASSISTANT_UNLOCK_CLICKS = 30;

export const AI_ASSISTANT_UNLOCK_EVENT = 'ai-assistant-unlocked';

/** Survives refresh in this tab; a new tab starts with the assistant hidden. */
export const AI_ASSISTANT_UNLOCK_KEY = 'aiAssistantUnlocked';

export const isAIAssistantUnlocked = () => {
  try {
    return sessionStorage.getItem(AI_ASSISTANT_UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
};

export const unlockAIAssistant = () => {
  try {
    sessionStorage.setItem(AI_ASSISTANT_UNLOCK_KEY, '1');
  } catch { /* private mode — the event still reveals it for this page load */ }
  window.dispatchEvent(new Event(AI_ASSISTANT_UNLOCK_EVENT));
};

/**
 * Knowledge base about the panel.
 * NOTE: seeded with basic panel info — replace/extend with the real Q&A
 * content when provided.
 */
export const KNOWLEDGE_BASE = [
  {
    id: 'dashboard',
    keywords: ['dashboard', 'home', 'metrics', 'analytics', 'لوحة', 'الرئيسية', 'احصائيات', 'إحصائيات'],
    answer: {
      en: 'The Dashboard shows your key metrics and analytics. Open it from the home tab in the bottom navigation bar.',
      ar: 'تعرض لوحة المعلومات المقاييس والتحليلات الرئيسية. يمكنك فتحها من تبويب الرئيسية في شريط التنقل السفلي.'
    }
  },
  {
    id: 'transactions',
    keywords: ['transaction', 'transfer', 'deposit', 'withdraw', 'balance', 'معاملة', 'معاملات', 'تحويل', 'ايداع', 'إيداع', 'سحب', 'رصيد'],
    answer: {
      en: 'Transactions are managed from the Transactions page. You can create deposits, withdrawals and transfers, and review the full transaction history there.',
      ar: 'تُدار المعاملات من صفحة المعاملات. يمكنك إنشاء عمليات إيداع وسحب وتحويل، ومراجعة سجل المعاملات الكامل هناك.'
    }
  },
  {
    id: 'agent-tree',
    keywords: ['agent tree', 'agents', 'hierarchy', 'tree', 'شجرة', 'وكلاء', 'وكيل', 'التسلسل'],
    answer: {
      en: 'The Agent Tree page shows the full hierarchy of agents and sub-agents. Click any agent to expand their downline and see their players.',
      ar: 'تعرض صفحة شجرة الوكلاء التسلسل الهرمي الكامل للوكلاء والوكلاء الفرعيين. اضغط على أي وكيل لتوسيع فروعه ورؤية لاعبيه.'
    }
  },
  {
    id: 'users',
    keywords: ['user', 'users', 'player', 'players', 'create user', 'مستخدم', 'مستخدمين', 'لاعب', 'لاعبين', 'انشاء مستخدم', 'إنشاء'],
    answer: {
      en: 'User management is on the Users page — you can create new users, change passwords, and open a deep view of any user for full details.',
      ar: 'إدارة المستخدمين موجودة في صفحة المستخدمين — يمكنك إنشاء مستخدمين جدد وتغيير كلمات المرور وفتح عرض تفصيلي لأي مستخدم.'
    }
  },
  {
    id: 'reports',
    keywords: ['report', 'reports', 'bet history', 'bets', 'تقرير', 'تقارير', 'سجل الرهانات', 'رهانات'],
    answer: {
      en: 'Reports and Bet History pages let you review betting activity, wins/losses and financial summaries for any period.',
      ar: 'تتيح لك صفحتا التقارير وسجل الرهانات مراجعة نشاط الرهانات والأرباح/الخسائر والملخصات المالية لأي فترة.'
    }
  },
  {
    id: 'limits',
    keywords: ['limit', 'limits', 'bet limit', 'حد', 'حدود', 'حد الرهان'],
    answer: {
      en: 'Betting limits are configured on the Limits page. You can set per-user bet limits there.',
      ar: 'يتم ضبط حدود الرهان من صفحة الحدود. يمكنك تعيين حدود الرهان لكل مستخدم هناك.'
    }
  },
  {
    id: 'settings',
    keywords: ['settings', 'language', 'dark mode', 'theme', 'password', 'اعدادات', 'إعدادات', 'لغة', 'الوضع الداكن', 'كلمة المرور', 'كلمة السر'],
    answer: {
      en: 'Settings open as a modal from the gear icon in the bottom bar. There you can change language, theme (dark mode) and your password.',
      ar: 'تُفتح الإعدادات كنافذة من أيقونة الترس في الشريط السفلي. يمكنك هناك تغيير اللغة والمظهر (الوضع الداكن) وكلمة المرور.'
    }
  }
];

const FALLBACK_ANSWER = {
  en: "I don't have an answer for that yet. My knowledge about the panel is still being expanded — try asking about the dashboard, transactions, agent tree, users, reports, limits or settings.",
  ar: 'ليس لدي إجابة على ذلك بعد. لا تزال معرفتي بلوحة التحكم قيد التوسيع — جرّب السؤال عن لوحة المعلومات أو المعاملات أو شجرة الوكلاء أو المستخدمين أو التقارير أو الحدود أو الإعدادات.'
};

const GREETING_KEYWORDS = ['hello', 'hi', 'hey', 'salam', 'مرحبا', 'مرحبًا', 'اهلا', 'أهلا', 'السلام عليكم', 'هلا'];

const GREETING_ANSWER = {
  en: "Hello! I'm the panel assistant. Ask me anything about the panel — you can type or use the microphone, in English or Arabic.",
  ar: 'مرحباً! أنا مساعد لوحة التحكم. اسألني أي شيء عن اللوحة — يمكنك الكتابة أو استخدام الميكروفون، بالإنجليزية أو العربية.'
};

/** Detect whether text is predominantly Arabic script */
export const detectLanguage = (text) => {
  const arabicChars = (text.match(/[\u0600-\u06FF\u0750-\u077F]/g) || []).length;
  const latinChars = (text.match(/[a-zA-Z]/g) || []).length;
  return arabicChars > latinChars ? 'ar' : 'en';
};

/** Normalize text for matching: lowercase, strip Arabic diacritics, unify alef forms */
const normalize = (text) =>
  (text || '')
    .toLowerCase()
    .replace(/[\u064B-\u065F\u0670]/g, '') // Arabic diacritics
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Answer a question from the knowledge base.
 * Scores entries by keyword hits and returns the best match's answer in the
 * question's language.
 *
 * @param {string} question
 * @returns {{ text: string, lang: 'en'|'ar', matched: boolean }}
 */
export const answerQuestion = (question) => {
  const lang = detectLanguage(question);
  const normalizedQuestion = normalize(question);

  if (GREETING_KEYWORDS.some((g) => normalizedQuestion === normalize(g) || normalizedQuestion.startsWith(normalize(g) + ' '))) {
    return { text: GREETING_ANSWER[lang] || GREETING_ANSWER.en, lang, matched: true };
  }

  let best = null;
  let bestScore = 0;

  for (const entry of KNOWLEDGE_BASE) {
    let score = 0;
    for (const keyword of entry.keywords) {
      const normalizedKeyword = normalize(keyword);
      if (normalizedKeyword && normalizedQuestion.includes(normalizedKeyword)) {
        // Longer keyword matches are more specific, weight them higher
        score += normalizedKeyword.split(' ').length * 2 + 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }

  if (best) {
    return { text: best.answer[lang] || best.answer.en, lang, matched: true };
  }

  return { text: FALLBACK_ANSWER[lang] || FALLBACK_ANSWER.en, lang, matched: false };
};
